import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { WORK_STRIP_PHOTOS, PHOTO_ALT, OBJECT_POS } from "./Gallery";

// Triple the list so the strip can loop seamlessly in both directions
const LOOP = [...WORK_STRIP_PHOTOS, ...WORK_STRIP_PHOTOS, ...WORK_STRIP_PHOTOS];

export const WorkCarousel = () => {
  const trackRef = useRef(null);
  const delayRef = useRef(null);
  const intervalRef = useRef(null);
  const settleRef = useRef(null);
  const pausedRef = useRef(false);
  const resumeRef = useRef(null);
  const posRef = useRef(0);
  const [selected, setSelected] = useState(null);

  // Start on the middle copy instantly (no animation) so both directions can scroll forever
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const id = requestAnimationFrame(() => {
      posRef.current = track.scrollWidth / 3;
      track.scrollLeft = posRef.current;
    });
    return () => cancelAnimationFrame(id);
  }, []);

  // Slow auto-drift; position tracked in a float ref because browsers round scrollLeft to whole pixels
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let raf;
    let last = 0;
    const step = (ts) => {
      raf = requestAnimationFrame(step);
      if (pausedRef.current) {
        last = ts;
        posRef.current = track.scrollLeft; // follow manual scrolling while paused
        return;
      }
      const dt = Math.min(ts - last, 60);
      last = ts;
      // If arrows/swipe moved the track, adopt that position first
      if (Math.abs(track.scrollLeft - posRef.current) > 2) posRef.current = track.scrollLeft;
      posRef.current += dt * 0.028; // ~28px per second — a slow drift
      const third = track.scrollWidth / 3;
      if (posRef.current >= third * 2) posRef.current -= third;
      track.scrollLeft = posRef.current;
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  const pause = () => {
    pausedRef.current = true;
    clearTimeout(resumeRef.current);
  };
  const resumeSoon = () => {
    clearTimeout(resumeRef.current);
    resumeRef.current = setTimeout(() => {
      pausedRef.current = false;
    }, 1500);
  };

  // A tap opens the photo without pausing the drift; only an actual swipe pauses it
  const downX = useRef(null);
  const swiped = useRef(false);
  const onTrackDown = (e) => {
    downX.current = e.clientX;
    swiped.current = false;
  };
  const onTrackMove = (e) => {
    if (downX.current === null) return;
    if (Math.abs(e.clientX - downX.current) > 10) {
      swiped.current = true;
      pause();
    }
  };
  const onTrackUp = () => {
    if (swiped.current) resumeSoon();
    downX.current = null;
  };

  // After manual scrolling settles, silently hop back into the middle copy
  const handleScroll = () => {
    clearTimeout(settleRef.current);
    settleRef.current = setTimeout(() => {
      const track = trackRef.current;
      if (!track) return;
      const third = track.scrollWidth / 3;
      if (track.scrollLeft < third) track.scrollLeft += third;
      else if (track.scrollLeft >= third * 2) track.scrollLeft -= third;
    }, 160);
  };

  const scrollOne = (dir) => {
    const track = trackRef.current;
    if (!track) return;
    const tile = track.querySelector("[data-tile]");
    const gap = window.innerWidth >= 1024 ? 12 : 4;
    const step = tile ? tile.getBoundingClientRect().width + gap : track.clientWidth;
    track.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  // Tap scrolls once; holding the arrow keeps scrolling until released
  const startHold = (dir) => {
    pause();
    stopHold();
    scrollOne(dir);
    delayRef.current = setTimeout(() => {
      intervalRef.current = setInterval(() => scrollOne(dir), 250);
    }, 400);
  };

  const stopHold = () => {
    clearTimeout(delayRef.current);
    clearInterval(intervalRef.current);
    delayRef.current = null;
    intervalRef.current = null;
  };

  const endHold = () => {
    stopHold();
    resumeSoon();
  };

  useEffect(
    () => () => {
      stopHold();
      clearTimeout(resumeRef.current);
      clearTimeout(settleRef.current);
    },
    []
  );

  const arrowClass =
    "absolute top-1/2 z-10 -translate-y-1/2 p-1 text-royal [filter:drop-shadow(0_0_3px_rgba(255,255,255,0.95))]";

  return (
    <>
      <div
        data-testid="work-carousel-section"
        className="relative w-full"
      >
        <button
          type="button"
          aria-label="Scroll to previous photos"
          data-testid="work-carousel-prev"
          onPointerDown={() => startHold(-1)}
          onPointerUp={endHold}
          onPointerLeave={endHold}
          onPointerCancel={endHold}
          onContextMenu={(e) => e.preventDefault()}
          onMouseEnter={pause}
          onMouseLeave={() => {
            pausedRef.current = false;
          }}
          className={`${arrowClass} left-0.5`}
        >
          <ChevronLeft size={32} />
        </button>

        <div
          ref={trackRef}
          onScroll={handleScroll}
          onPointerDown={onTrackDown}
          onPointerMove={onTrackMove}
          onPointerUp={onTrackUp}
          onPointerCancel={onTrackUp}
          data-testid="work-carousel-track"
          className="flex w-full gap-1 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] lg:gap-3 [&::-webkit-scrollbar]:hidden"
        >
          {LOOP.map((src, i) => {
            const file = src.split("/").pop();
            return (
              <button
                key={`${src}-${i}`}
                type="button"
                data-tile
                data-testid={`work-carousel-photo-${(i % WORK_STRIP_PHOTOS.length) + 1}`}
                onClick={() => setSelected(src)}
                className="w-[calc(25%-4px)] flex-none cursor-pointer lg:w-[calc(16.666%-12px)]"
              >
                <img
                  src={src}
                  alt={PHOTO_ALT[file] || "Trumbull Paving project photo"}
                  loading="lazy"
                  draggable="false"
                  style={{ objectPosition: OBJECT_POS[file] || "50% 50%" }}
                  className="aspect-square w-full border border-black/15 object-cover"
                />
              </button>
            );
          })}
        </div>

        <button
          type="button"
          aria-label="Scroll to next photos"
          data-testid="work-carousel-next"
          onPointerDown={() => startHold(1)}
          onPointerUp={endHold}
          onPointerLeave={endHold}
          onPointerCancel={endHold}
          onContextMenu={(e) => e.preventDefault()}
          onMouseEnter={pause}
          onMouseLeave={() => {
            pausedRef.current = false;
          }}
          className={`${arrowClass} right-0.5`}
        >
          <ChevronRight size={32} />
        </button>
      </div>

      {selected &&
        createPortal(
          <div
            data-testid="work-lightbox"
            onClick={() => setSelected(null)}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-6"
          >
            <button
              data-testid="work-lightbox-close"
              onClick={() => setSelected(null)}
              className="absolute right-6 top-6 text-white/80 transition-colors duration-200 hover:text-white"
              aria-label="Close"
            >
              <X className="h-8 w-8" />
            </button>
            <img
              src={selected}
              alt="Enlarged work photo"
              className="max-h-[85vh] max-w-full border border-white/20 object-contain"
            />
          </div>,
          document.body
        )}
    </>
  );
};
